import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, CheckCircle, Linkedin, Phone, MessageCircle, ArrowRight } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after success
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    }, 3000);
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24 pt-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <MessageCircle className="w-16 h-16 text-cresento-accent mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-cresento-light to-cresento-accent bg-clip-text text-transparent">
              Get in Touch
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Ready to transform your game? Have questions? Our team is here to help you discover the power of Cresento.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-cresento-dark/40 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Contact Form */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl sm:text-4xl font-bold mb-6 bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">Send us a Message</h2>
                <p className="text-gray-300 mb-8 text-lg">
                  Whether you're interested in our smart shin pads, have technical questions, or want to explore partnerships, we'd love to hear from you.
                </p>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-cresento-light mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 bg-cresento-primary/20 backdrop-blur border border-cresento-secondary/30 rounded-lg focus:outline-none focus:border-cresento-secondary transition-colors text-white placeholder-gray-300"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-cresento-light mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-6 py-4 bg-cresento-primary/20 backdrop-blur border border-cresento-secondary/30 rounded-lg focus:outline-none focus:border-cresento-secondary transition-colors text-white placeholder-gray-300"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-cresento-light mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-6 py-4 bg-cresento-primary/20 backdrop-blur border border-cresento-secondary/30 rounded-lg focus:outline-none focus:border-cresento-secondary transition-colors resize-none text-white placeholder-gray-300"
                      placeholder="Tell us about your inquiry..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting || isSubmitted}
                    whileHover={{ scale: isSubmitting || isSubmitted ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting || isSubmitted ? 1 : 0.98 }}
                    className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all ${
                      isSubmitted
                        ? 'bg-green-600 text-white'
                        : isSubmitting
                        ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-cresento-secondary to-cresento-accent hover:from-cresento-secondary/90 hover:to-cresento-accent/90 text-white'
                    }`}
                  >
                    {isSubmitted ? (
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Message Sent!
                      </div>
                    ) : isSubmitting ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Send className="w-5 h-5" />
                        Send Message
                      </div>
                    )}
                  </motion.button>
                </form>
              </motion.div>

              {/* Contact Information */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold mb-6 bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent">Multiple Ways to Connect</h2>
                  <p className="text-gray-300 mb-8 text-lg">
                    Choose the method that works best for you. We're committed to providing quick, helpful responses to all inquiries.
                  </p>
                </div>

                <div className="space-y-6">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cresento-secondary to-cresento-accent rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">General Inquiries</h3>
                        <a 
                          href="mailto:hello@cresento.com" 
                          className="text-cresento-accent hover:text-cresento-secondary transition-colors text-lg"
                        >
                          hello@cresento.com
                        </a>
                        <p className="text-gray-300 mt-2">For general questions about our products and services</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cresento-accent to-cresento-secondary rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Partnerships & Investors</h3>
                        <a 
                          href="mailto:partnerships@cresento.com" 
                          className="text-cresento-accent hover:text-cresento-secondary transition-colors text-lg"
                        >
                          partnerships@cresento.com
                        </a>
                        <p className="text-gray-300 mt-2">For business partnerships and investment opportunities</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cresento-secondary to-cresento-accent rounded-full flex items-center justify-center flex-shrink-0">
                        <Linkedin className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-white mb-2">Social Media</h3>
                        <a 
                          href="https://linkedin.com/company/cresento" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cresento-accent hover:text-cresento-secondary transition-colors text-lg"
                        >
                          Connect on LinkedIn
                        </a>
                        <p className="text-gray-300 mt-2">Follow us for updates and industry insights</p>
                      </div>
                    </div>
                  </motion.div>
                </div>

                <div className="bg-gradient-to-br from-cresento-primary/20 to-cresento-dark/30 rounded-2xl p-6 mt-8 border border-cresento-secondary/30">
                  <h3 className="text-xl font-semibold text-cresento-accent mb-3">Response Time</h3>
                  <p className="text-gray-300">
                    We typically respond to all inquiries within 24-48 hours during business days. 
                    For urgent matters, please mention it in your message.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Sign Up for Updates Section */}
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-r from-cresento-secondary to-cresento-accent">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Mail className="text-white mx-auto mb-6" size={64} />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
              Stay in the Loop
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Be the first to know about product launches, new features, and exclusive early access opportunities.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                required
                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur border border-white/20 rounded-lg text-white placeholder-gray-200 focus:outline-none focus:border-white/40 transition-all duration-300"
              />
              <button type="submit" className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
                <ArrowRight className="mr-2 h-5 w-5" />
                Sign Up for Updates
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;